import React from "react";
import { useParams } from "react-router-dom";
import { db } from "../firebase/config";
import { useState, useEffect } from "react";
import { collection, query, onSnapshot } from "firebase/firestore";
import Map from "../components/Map";

const TrackOrder = () => {
  const params = useParams();
  const { id } = params;
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const q = query(collection(db, "tracking"));

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        if (!querySnapshot.empty) {
          const data = querySnapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
          const tracking = data.find((item) => item.id === id);
          setTrackingData(tracking || null);
        }
        setLoading(false);
      });
      return () => unsubscribe();
    } catch (err) {
      console.log(err.message);
      setLoading(false);
    }
  }, [id]);

  // Modern inline styles
  const styles = {
    pageContainer: {
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      backgroundColor: "#f9fafb",
      minHeight: "100vh",
      color: "#1f2937",
    },
    heroSection: {
      backgroundImage: `url(https://images.unsplash.com/photo-1485575301924-6891ef935dcd?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80)`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      padding: "5rem 1rem",
      textAlign: "center",
      position: "relative",
    },
    heroOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
    heroTitle: {
      color: "#ffffff",
      fontSize: "2.5rem",
      fontWeight: "bold",
      position: "relative",
      zIndex: 1,
      margin: 0,
    },
    contentWrapper: {
      maxWidth: "1000px",
      margin: "-3rem auto 3rem",
      padding: "0 1rem",
      position: "relative",
      zIndex: 2,
    },
    card: {
      backgroundColor: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
      padding: "1rem",
      marginBottom: "2rem",
    },
    orderIdText: {
      fontSize: "1.5rem",
      fontWeight: "700",
      color: "#111827",
      borderBottom: "2px solid #f3f4f6",
      paddingBottom: "1rem",
      marginBottom: "1.5rem",
    },
    sectionTitle: {
      fontSize: "1.25rem",
      fontWeight: "600",
      color: "#374151",
      marginBottom: "1rem",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    progressBarContainer: {
      marginBottom: "2.5rem",
    },
    progressBarHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "0.5rem",
      fontWeight: "600",
      color: "#4b5563",
    },
    progressBarTrack: {
      width: "100%",
      height: "1.5rem",
      backgroundColor: "#e5e7eb",
      borderRadius: "9999px",
      overflow: "hidden",
    },
    progressBarFill: (percentage) => ({
      width: `${percentage}%`,
      height: "100%",
      backgroundColor: percentage === 100 ? "#10b981" : "#3b82f6",
      transition: "width 1s ease-in-out",
    }),
    infoGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: "1.5rem",
      marginBottom: "2rem",
    },
    infoBox: {
      backgroundColor: "#f9fafb",
      padding: "1rem",
      borderRadius: "8px",
      border: "1px solid #e5e7eb",
    },
    infoLabel: {
      fontSize: "0.875rem",
      color: "#6b7280",
      marginBottom: "0.25rem",
      fontWeight: "500",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    infoValue: {
      fontSize: "1rem",
      color: "#111827",
      fontWeight: "500",
    },
    tableWrapper: {
      overflowX: "auto",
      borderRadius: "8px",
      border: "1px solid #e5e7eb",
      marginBottom: "2rem",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      textAlign: "left",
      whiteSpace: "nowrap",
    },
    th: {
      backgroundColor: "#f3f4f6",
      padding: "1rem",
      fontSize: "0.875rem",
      fontWeight: "600",
      color: "#374151",
      borderBottom: "1px solid #e5e7eb",
    },
    td: {
      padding: "1rem",
      fontSize: "0.875rem",
      color: "#4b5563",
      borderBottom: "1px solid #e5e7eb",
    },
    extraDetailsWrapper: {
      display: "flex",
      flexDirection: "column",
      gap: "1rem",
    },
    extraDetailCard: {
      backgroundColor: "#eff6ff",
      border: "1px solid #bfdbfe",
      borderLeft: "4px solid #3b82f6",
      padding: "1.25rem",
      borderRadius: "6px",
    },
    mapContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      marginTop: "2rem",
      border: "1px solid #e5e7eb",
    },
    noDataText: {
      textAlign: "center",
      color: "#6b7280",
      padding: "2rem",
      fontStyle: "italic",
    }
  };

  if (loading) {
    return (
      <div style={{ ...styles.pageContainer, display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <h2 style={{ color: "#3b82f6" }}>Loading tracking information...</h2>
      </div>
    );
  }

  if (!trackingData) {
    return (
      <div style={styles.pageContainer}>
        <div style={styles.heroSection}>
          <div style={styles.heroOverlay}></div>
          <h2 style={styles.heroTitle}>Tracking Not Found</h2>
        </div>
        <div style={styles.contentWrapper}>
          <div style={styles.card}>
            <p style={styles.noDataText}>We couldn't find an order with the ID: {id}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      {/* Hero Section */}
      <div style={styles.heroSection}>
        <div style={styles.heroOverlay}></div>
        <h2 style={styles.heroTitle}>Track Your Order</h2>
      </div>

      <div style={styles.contentWrapper}>
        <div style={styles.card}>
          <h3 style={styles.orderIdText}>Order ID: <span style={{ color: "#3b82f6" }}>{id}</span></h3>

          {/* Progress Bar */}
          <div style={styles.progressBarContainer}>
            <div style={styles.progressBarHeader}>
              <span>Delivery Progress</span>
              <span style={{ color: trackingData.percentage === 100 ? "#10b981" : "#3b82f6" }}>
                {trackingData.percentage || 0}%
              </span>
            </div>
            <div style={styles.progressBarTrack}>
              <div style={styles.progressBarFill(trackingData.percentage || 0)}></div>
            </div>
          </div>

          {/* Client Details */}
          <h4 style={styles.sectionTitle}>
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            Client Information
          </h4>

          <div style={styles.infoGrid}>
            {(trackingData.fullname || trackingData.email || trackingData.phone || trackingData.location) ? (
              <>
                {trackingData.fullname && (
                  <div style={styles.infoBox}>
                    <div style={styles.infoLabel}>Full Name</div>
                    <div style={styles.infoValue}>{trackingData.fullname}</div>
                  </div>
                )}
                {trackingData.email && (
                  <div style={styles.infoBox}>
                    <div style={styles.infoLabel}>Email</div>
                    <div style={styles.infoValue}>{trackingData.email}</div>
                  </div>
                )}
                {trackingData.phone && (
                  <div style={styles.infoBox}>
                    <div style={styles.infoLabel}>Phone Number</div>
                    <div style={styles.infoValue}>{trackingData.phone}</div>
                  </div>
                )}
                {trackingData.location && (
                  <div style={styles.infoBox}>
                    <div style={styles.infoLabel}>Location</div>
                    <div style={styles.infoValue}>{trackingData.location}</div>
                  </div>
                )}
              </>
            ) : trackingData.clientDetails ? (
              <div style={{ ...styles.infoBox, gridColumn: "1 / -1" }}>
                <div style={styles.infoLabel}>Details</div>
                <div style={styles.infoValue}>{trackingData.clientDetails}</div>
              </div>
            ) : (
              <div style={{ ...styles.infoBox, gridColumn: "1 / -1" }}>
                <div style={{ color: "#6b7280", fontStyle: "italic" }}>No client information provided.</div>
              </div>
            )}
          </div>

          {/* Extra Details */}
          {trackingData.extra && trackingData.extra.length > 0 && (
            <div style={{ marginBottom: "2.5rem" }}>
              <div style={styles.extraDetailsWrapper}>
                {trackingData.extra.map((item, index) => (
                  <div key={index} style={styles.extraDetailCard}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem", color: "#1e40af", fontWeight: "600" }}>
                      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="16" x2="12" y2="12"></line>
                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                      </svg>
                      Note ({item.pDate} at {item.pTime})
                    </div>
                    <div style={{ color: "#1e3a8a", fontSize: "0.95rem", lineHeight: "1.5" }}>
                      {item.pComment}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Shipment History */}
          <h4 style={styles.sectionTitle}>
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            Shipment History
          </h4>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date & Time</th>
                  <th style={styles.th}>Location</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {trackingData.shipping && trackingData.shipping.length > 0 ? (
                  trackingData.shipping.map((item, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#f9fafb" }}>
                      <td style={styles.td}>{item.time}</td>
                      <td style={styles.td}>{item.location}</td>
                      <td style={styles.td}>
                        <span style={{
                          padding: "0.25rem 0.75rem",
                          borderRadius: "9999px",
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          backgroundColor: item.status.toLowerCase().includes("delivered") ? "#d1fae5" : "#dbeafe",
                          color: item.status.toLowerCase().includes("delivered") ? "#065f46" : "#1e40af"
                        }}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" style={styles.td}>
                      <div style={styles.noDataText}>No shipment history available yet.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Packages */}
          <h4 style={styles.sectionTitle}>
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
              <line x1="12" y1="22.08" x2="12" y2="12"></line>
            </svg>
            Package Information
          </h4>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Qty</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Description</th>
                  <th style={styles.th}>Length</th>
                  <th style={styles.th}>Width</th>
                </tr>
              </thead>
              <tbody>
                {trackingData.packaging && trackingData.packaging.length > 0 ? (
                  trackingData.packaging.map((item, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#f9fafb" }}>
                      <td style={styles.td}>{item.PQuantity}</td>
                      <td style={styles.td}>{item.PType}</td>
                      <td style={styles.td}>{item.PDesc}</td>
                      <td style={styles.td}>{item.PLength}</td>
                      <td style={styles.td}>{item.PWidth}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={styles.td}>
                      <div style={styles.noDataText}>No package details available.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Map Section */}
          {trackingData.mapLink && (
            <>
              <h4 style={{ ...styles.sectionTitle, marginTop: "2rem" }}>
                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
                  <line x1="8" y1="2" x2="8" y2="18"></line>
                  <line x1="16" y1="6" x2="16" y2="22"></line>
                </svg>
                Live Location
              </h4>
              <div style={styles.mapContainer}>
                <Map googleMapLink={trackingData.mapLink} />
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default TrackOrder;